import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  StyleSheet,
  Keyboard,
  Platform,
} from "react-native";
 
import { GOOGLE_API_KEY } from "@env";

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Base URL for Google Places API (use proxy if needed)
const API_BASE_URL = "https://maps.googleapis.com/maps/api/place";

const GooglePlacesAutoCompleteApp = ({
  value = "",
  onChange,
  onAddressSelect,
  placeholder = "Enter address",
  disabled = false,
  required = false,
  name = "address",
  label = "Address",
  validate = false,
  forceValidation = false,
}) => {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const lastSelectedValue = useRef("");
  const lastInputValue = useRef("");
  const isSelecting = useRef(false);
  const hasUserModified = useRef(false);
  const inputRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    let newValue = "";
    let propValue = value;
    if (propValue == "[object Object]") propValue = "";
    if (typeof propValue === "string") {
      newValue = propValue;
    } else if (
      propValue &&
      typeof propValue === "object" &&
      propValue.fullAddress
    ) {
      newValue = propValue.fullAddress;
    } else {
      console.warn("Invalid value prop:", propValue);
    }
    if (
      newValue !== inputValue &&
      newValue !== lastSelectedValue.current &&
      !isSelecting.current &&
      !hasUserModified.current &&
      (newValue || !lastSelectedValue.current)
    ) {
      setInputValue(newValue);
      lastSelectedValue.current = newValue;
      setShowSuggestions(false);
    }
  }, [value, inputValue]);

  // Google Places Autocomplete API search
  const searchAddress = async (query) => {
    if (!isFocused || query.length < 3 || isSelecting.current) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoadingAddress(true);

    try {
      const autocompleteResponse = await fetch(
        `${API_BASE_URL}/autocomplete/json?input=${encodeURIComponent(
          query
        )}&types=address&components=country:us&key=${GOOGLE_API_KEY}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!autocompleteResponse.ok) {
        throw new Error(`HTTP error! Status: ${autocompleteResponse.status}`);
      }

      const autocompleteData = await autocompleteResponse.json();
      if (autocompleteData.status !== "OK") {
        throw new Error(`Google Places API error: ${autocompleteData.status}`);
      }

      const formattedSuggestions = await Promise.all(
        autocompleteData.predictions.map(async (item) => {
          try {
            const detailsResponse = await fetch(
              `${API_BASE_URL}/details/json?place_id=${item.place_id}&fields=address_components,geometry&key=${GOOGLE_API_KEY}`,
              {
                headers: {
                  Accept: "application/json",
                },
              }
            );
            if (!detailsResponse.ok) {
              throw new Error(`Details API error: ${detailsResponse.status}`);
            }
            const detailsData = await detailsResponse.json();
            if (detailsData.status !== "OK") {
              return null;
            }

            const addressComponents = detailsData.result.address_components;
            const getComponent = (type) =>
              addressComponents.find((comp) => comp.types.includes(type))
                ?.long_name || "";
            const getStateComponent = (type) =>
              addressComponents.find((comp) => comp.types.includes(type))
                ?.short_name || "";

            return {
              display_name: item.description,
              lat: detailsData.result.geometry.location.lat,
              lon: detailsData.result.geometry.location.lng,
              address: {
                house_number: getComponent("street_number"),
                road: getComponent("route"),
                city:
                  getComponent("locality") ||
                  getComponent("sublocality") ||
                  getComponent("administrative_area_level_3") ||
                  "",
                state: getStateComponent("administrative_area_level_1"),
                postcode: getComponent("postal_code") || "",
              },
            };
          } catch (error) {
            console.error(
              `Error fetching details for place_id ${item.place_id}:`,
              error
            );
            return null;
          }
        })
      );

      const validSuggestions = formattedSuggestions.filter(
        (item) => item !== null
      );
      setAddressSuggestions(validSuggestions);
      setShowSuggestions(validSuggestions.length > 0 && isFocused);
    } catch (error) {
      console.error("Address search error:", error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const debouncedSearch = useCallback(debounce(searchAddress, 500), [
    isFocused,
  ]);

  const handleAddressInputChange = (text) => {
    setInputValue(text);
    hasUserModified.current = true;
    isSelecting.current = false;
    if (onChange) {
      onChange({ target: { name, value: text } });
    }
    if (text !== lastInputValue.current) {
      debouncedSearch(text);
    }
    setSelectedAddressIndex(-1);
  };

  const handleAddressSelect = (suggestion) => {
    if (!suggestion || !suggestion.address) {
      console.error("Invalid suggestion:", suggestion);
      return;
    }
    isSelecting.current = true;
    hasUserModified.current = false;

    const formattedAddress =
      `${suggestion.address.house_number || ""} ${
        suggestion.address.road || ""
      }`.trim() || suggestion.display_name;

    const addressData = {
      fullAddress: formattedAddress,
      streetAddress: formattedAddress,
      city: suggestion.address.city,
      state: suggestion.address.state,
      zipCode: suggestion.address.postcode,
      lat: suggestion.lat,
      lon: suggestion.lon,
      displayName: suggestion.display_name,
    };

    setInputValue(formattedAddress);
    lastSelectedValue.current = formattedAddress;
    lastInputValue.current = formattedAddress;

    setTimeout(() => {
      if (onChange) {
        onChange({ target: { name, value: formattedAddress } });
      }
      if (onAddressSelect) {
        onAddressSelect(addressData);
      }
    }, 0);

    setAddressSuggestions([]);
    setShowSuggestions(false);
    setSelectedAddressIndex(-1);
    isSelecting.current = false;
    Keyboard.dismiss();
  };

  const handleKeyPress = ({ nativeEvent: { key } }) => {
    if (!showSuggestions || addressSuggestions.length === 0) return;

    switch (key) {
      case "ArrowDown":
        setSelectedAddressIndex((prev) =>
          prev < addressSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        setSelectedAddressIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        if (selectedAddressIndex >= 0) {
          hasUserModified.current = false;
          handleAddressSelect(addressSuggestions[selectedAddressIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setSelectedAddressIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleBlur = () => {
    if (isSelecting.current) return;
    setIsFocused(false);
    setShowSuggestions(false);
    setAddressSuggestions([]);
    if (lastSelectedValue.current && !inputValue) {
      const restoredValue =
        typeof lastSelectedValue.current === "string"
          ? lastSelectedValue.current
          : "";
      setInputValue(restoredValue);
      if (onChange) {
        onChange({ target: { name, value: restoredValue } });
      }
    }
    hasUserModified.current = false;
    lastInputValue.current = inputValue;
  };

  const renderSuggestion = ({ item, index }) => (
    <TouchableOpacity
      onPress={() => handleAddressSelect(item)}
      style={[
        styles.suggestionItem,
        selectedAddressIndex === index && styles.selectedSuggestion,
        index === addressSuggestions.length - 1 && styles.lastSuggestion,
      ]}
    >
      <Text style={styles.suggestionText}>{item.display_name}</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <View 
      style={[
        styles.inputBoxContainer,
        showSuggestions && addressSuggestions.length > 0 && { height: 320 },
      ]}
      >
        {label && (
          <Text
            style={[
              styles.inputBoxLabel,
              (isFocused || !!inputValue) && styles.inputBoxLabelFocused,
            ]}
          >
            {label}
          </Text>
        )}
        <TextInput
          ref={inputRef}
          value={typeof inputValue === "string" ? inputValue : ""}
          onChangeText={handleAddressInputChange}
          onKeyPress={handleKeyPress}
          onBlur={handleBlur}
          onFocus={() => {
            setIsFocused(true);
            lastInputValue.current = inputValue;
          }}
          placeholder={placeholder}
          placeholderTextColor="#999"
          style={[styles.inputBoxOverride, isFocused && styles.inputBoxFocus]}
          editable={!disabled}
          autoCapitalize="none"
          autoComplete="off"
        />
        {isLoadingAddress && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#666" />
          </View>
        )}
        {showSuggestions && addressSuggestions.length > 0 && (
          <View style={styles.posStyle}>
            <FlatList
            data={addressSuggestions}
            renderItem={renderSuggestion}
            keyExtractor={(item, index) => `${item.display_name}-${index}`}
            style={styles.suggestionsContainer} 
            keyboardShouldPersistTaps="handled"
            ListFooterComponent={
              <View style={styles.attributionContainer}>
                <Text style={styles.attributionText}>
                  Powered by Google Places
                </Text>
              </View>
            }
          /></View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    position: "relative",
    zIndex: 1000, // Increased to avoid clipping
    overflow: "visible",
     
    
  },
  inputBoxContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#c0c0c0",
    borderRadius: 8,
    padding: 8,
    marginBottom: 20,
    backgroundColor: "#fff",
    width: "100%",
    overflow: "visible",
    zIndex: 2000,
     
     
  },
  inputBoxLabel: {
    position: "absolute",
    backgroundColor: "#fff",
    top: -12,
    left: 3,
    fontSize: 14,
    color: "gray",
    paddingHorizontal: 3,
    zIndex: 1,
    fontWeight: "bold",
    fontFamily: "Helvetica",
  },
  inputBoxLabelFocused: {
    top: -12,
    fontSize: 14,
    color: "gray",
  },
  inputBoxOverride: {
    //flex: 1,
    borderWidth: 0,
    height: 34,
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderRadius: 5,
    color: "rgb(81, 87, 93)",
    padding: 5,
    fontSize: 16,
    fontFamily: "Helvetica",
    width:"100%",
    
    
     
  },
  inputBoxFocus: {
    borderWidth: 1,
    borderColor: "#66afe9",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderRadius: 5,
    color: "rgb(81, 87, 93)",
    padding: 5, 
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 0, 0, 0.4)",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.8,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  loadingContainer: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: [{ translateY: -10 }],
  },
  suggestionsContainer: {
    position: "relative",
    top: 50, // Adjust based on input field height
    left: 0,
    
    right: 0,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#cccccc",
    borderTopWidth: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    maxHeight: 250,
    
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 15,
    zIndex: 9999,
    //backgroundColor:"yellow"
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0", 
    
  },
  lastSuggestion: {
    borderBottomWidth: 0,
  },
  selectedSuggestion: {
    backgroundColor: "#f0f0f0",
  },
  suggestionText: {
    fontSize: 15,
    color: "#333333",
    lineHeight: 24,
    fontFamily: "Helvetica",
  },
  attributionContainer: {
    padding: 6,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    backgroundColor: "#f9f9f9",
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  attributionText: {
    fontSize: 10,
    color: "#666",
    textAlign: "center",
  },
  posStyle: {
    position :"fixed",
    left: -330,
    right:0,
    zIndex:9999
     
  }
});

export default GooglePlacesAutoCompleteApp;